import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Award, GraduationCap, Dumbbell } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { site } from "@/data/site";

const CREDS = [
  { icon: Award, label: "ACE Certified Personal Trainer" },
  { icon: GraduationCap, label: "B.S. Exercise Science" },
  { icon: Dumbbell, label: "Years of 1:1 Coaching Experience" },
];

export default function CoachBio() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="coach" ref={ref} className="relative py-28 lg:py-40" data-testid="coach-bio-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="relative lg:col-span-6">
            <Reveal>
              <div className="relative overflow-hidden border-2 border-white/10 clip-diagonal">
                <motion.img
                  style={{ y: imgY }}
                  src="/coach.jpeg"
                  alt="Chris — motorsport strength and conditioning coach"
                  className="h-80 w-full scale-110 object-cover object-[center_15%] grayscale-[15%] sm:h-96 lg:h-[30rem]"
                />
                <div className="absolute inset-0 bg-purple/15 mix-blend-overlay" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#08080A]/70 to-transparent" />
                <div className="halftone-purple absolute inset-0 opacity-50" />
                <p className="absolute left-6 top-6 -rotate-3 font-display text-xl font-black italic uppercase text-gold drop-shadow-[0_0_15px_rgba(255,215,0,0.7)]">
                  Coach behind the program
                </p>
              </div>
              <div className="absolute -right-3 -top-3 -z-10 h-full w-full border-2 border-gold/40" />
            </Reveal>
          </div>

          <div className="lg:col-span-6 lg:pl-8">
            <Reveal>
              <p className="mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-gold">
                <span className="h-px w-10 bg-gold" />
                Your Coach
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-5xl lg:text-6xl">
                Meet <span className="text-purple">Chris.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-lg text-base font-medium leading-relaxed text-white/70 lg:text-lg">
                The coach behind Chrisjstperformance. Chris built this program
                to bring real, structured strength and conditioning to drivers —
                not generic gym plans, but training measured against what the
                car actually demands.
              </p>
            </Reveal>

            <div className="mt-10 flex flex-col gap-4">
              {CREDS.map((cred, i) => (
                <Reveal key={cred.label} delay={0.25 + i * 0.08}>
                  <div
                    className="flex items-center gap-4 border-2 border-white/12 bg-surface p-5 transition-colors duration-200 hover:border-purple"
                    data-testid={`coach-credential-${i}`}
                  >
                    <span className={`flex h-11 w-11 shrink-0 items-center justify-center border-2 ${i % 2 === 1 ? "border-gold/50 text-gold" : "border-purple/50 text-purple"}`}>
                      <cred.icon className="h-5 w-5" />
                    </span>
                    <span className="font-display text-sm font-black uppercase tracking-[0.1em] text-white sm:text-base">
                      {cred.label}
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.5}>
              <p className="mt-8 text-xs font-bold uppercase tracking-[0.2em] leading-relaxed text-white/40">
                Coaching drivers across karting, sim, autocross, endurance, and rally
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
